<template>
  <RxkDialog
    v-model="visible"
    title="关于调整产品城市最低价格标准的通知"
    width="1000px"
    :isLoading="true"
    confirmBtn="一键调价"
    cancelBtn="知道了，明日再提醒"
    :disableConfirm="disableConfirm"
    @sure="sure"
    @close="close"
  >
    <div class="city-price-content">
      <p>亲爱的合作伙伴，您好！</p>
      <p>
        <span>
          我们关注到，部分核心城市的流量规模与价值显著提升。为了帮助您在竞争中获得更大优势，并确保您的产品投放能匹配
        </span>
        <span class="tw-font-bold">更高质量的用户</span>
        <span>，平台将依据最新数据，实施新的城市产品最低价格标准。</span>
      </p>
      <p>
        ⏰
        <span class="tw-font-bold">调整时间：</span>
        {{ adjustTimeText }}
      </p>
      <p>
        🚨
        <span class="tw-font-bold">请注意：</span>
        届时，价格未达到新标准的产品会被暂时下线，影响您的订单哦！
      </p>
      <p class="tw-font-bold">建议您立即行动：</p>
      <p>
        • 点击
        <span class="tw-font-bold">【一键调价】</span>
        ，系统将立即帮您快速搞定，省心省力。
      </p>
      <p>• 或进入联盟产品，手动将产品价格调整至新标准以上。</p>
      <p>感谢您的支持，我们始终致力于助力您的业务增长！</p>
      <div class="tw-mt-4 table-box">
        <RxkVTable
          :data="tableData"
          @register="registerTable"
          @pageChange="handlePageChange"
        ></RxkVTable>
      </div>
    </div>
  </RxkDialog>
</template>
<script lang="ts" setup>
import { RxkDialog } from '@/components/common/RxkDialog'
import { useGlobalStore } from '@/stores/modules/global'
import { RxkVTable } from '@/components/common/RxkVTable'
import { useTable } from '@/components/common/RxkVTable/src/hooks/useTable'
import { ColumnType } from '@/types/table'
import {
  adjustCityPrice,
  adjustCityPriceConfirm,
  productPriceAdjustQuery,
  adjustCityPriceClose
} from '@/apis/marketingSystem/productManage'
import dayjs from 'dayjs'
import { useAccountStore } from '@/stores/modules/account'

const accountStore = useAccountStore()

const globalStore = useGlobalStore()

const visible = computed({
  get: () => {
    return globalStore.showCityPriceDialog
  },
  set: (val: boolean) => {
    globalStore.setShowCityPriceDialog(val)
  }
})

// 调价时间
const adjustId = ref('')
const adjustTime = ref('')
const adjustTimeText = computed(() => {
  if (!adjustTime.value) {
    return '--'
  }
  return dayjs(adjustTime.value).format('YYYY年MM月DD日 HH:mm:ss')
})
const disableConfirm = ref(false)

async function sure(fn: Fn) {
  ElMessageBox.confirm('确认是否一键调价？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      await adjustCityPrice(globalStore.cityPriceAdjustId)
      ElMessage.success('操作成功！')
      visible.value = false
    })
    .finally(() => {
      fn?.()
    })
}

function close(type: 'icon' | 'btn') {
  try {
    if (type === 'btn') {
      adjustCityPriceConfirm()
    }
    if (type === 'icon') {
      adjustCityPriceClose(adjustId.value)
    }
  } finally {
    globalStore.setShowCityPriceDialog(false)
    globalStore.setCityPriceAdjustId('')
  }
}

watch(
  () => accountStore?.userInfo?.id,
  async val => {
    if (val) {
      setTimeout(async () => {
        try {
          const res = await productPriceAdjustQuery()
          if (res?.id) {
            adjustId.value = res?.id
            globalStore.setShowCityPriceDialog(true)
            globalStore.setCityPriceAdjustId(adjustId.value)
            nextTick(() => {
              adjustTime.value = res?.adjustTime
              disableConfirm.value = !res.valid // valid为true才可以点击一件调价
              originalTableData.value = res?.productAdjustInfos || []
              resetPagination()
            })
          }
        } catch (error) {
          console.log(error)
        }
      }, 3000)
    }
  },
  {
    immediate: true
  }
)

watch(
  () => globalStore.cityPriceAdjustId,
  val => {
    adjustId.value = val
    getInfoById()
  }
)

async function getInfoById() {
  if (!adjustId.value) {
    return
  }
  try {
    const res = await productPriceAdjustQuery(adjustId.value)
    if (res?.id) {
      adjustTime.value = res?.adjustTime
      originalTableData.value = res?.productAdjustInfos || []
      disableConfirm.value = !res.valid // valid为true才可以点击一件调价
      resetPagination()
    }
  } catch (error) {
    console.log(error)
  }
}

// ------------------------------ 表格相关👇-------------------------
const columns: ColumnType[] = [
  {
    title: '产品名称',
    key: 'productName'
  },
  {
    title: '获客城市',
    key: 'city'
  },
  {
    title: '当前产品价格',
    key: 'price'
  },
  {
    title: '调整后最低价格标准',
    key: 'adjustPrice'
  }
]

const tableData = ref([])
const originalTableData = ref([])

const resetPagination = () => {
  setPaginationInfo({
    pageSize: 10,
    pageNum: 1
  })
  setTotal(originalTableData.value.length)
  splitTableData()
}

const [registerTable, { getPaginationInfo, setPaginationInfo, setTotal }] = useTable({
  columns: columns,
  searchInfo: {},
  showSelection: false,
  showRowIndex: false
})

const pageSize = ref(getPaginationInfo()?.pageSize || 10)
const pageNum = ref(getPaginationInfo()?.pageNum || 1)

function handlePageChange(val: number, type: 'num' | 'size') {
  if (type === 'num') {
    pageNum.value = val
  } else if (type === 'size') {
    pageNum.value = 1
    pageSize.value = val
  }
  setPaginationInfo({
    pageSize: pageSize.value,
    pageNum: pageNum.value
  })
  splitTableData()
}

function splitTableData() {
  const start = (pageNum.value - 1) * pageSize.value
  const end = start + pageSize.value
  tableData.value = originalTableData.value.slice(start, end)
}
</script>

<style lang="scss" scoped>
.city-price-content {
  padding: 8px 24px;
  p {
    margin: 0px;
    font-size: 14px;
    line-height: 24px;
  }
  .table-box {
    margin: 10px -20px;
    height: 300px;
  }
}
</style>
